import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import BillingSettingsTab from './BillingSettingsTab';
import BillingItemsTab from './BillingItemsTab';
import BillingSchemesTab from './BillingSchemesTab';
import { BillingSetupSubTab } from '../../../../common/enums/common.enum';
import { getActiveBillingItems, getBillingSchemes, getBillingSettings } from '../../../../api/invoicing.api';
import { getGroupsWithLocationName } from '../../../../api/kindergarten.api';
import { palette } from '../../../../theme';
import { MISSING_LABELS } from './settingsLabels';

interface BillingSetupTabProps {
  kindergartenId: string;
  subTab: BillingSetupSubTab;
  onSubTabChange: (subTab: BillingSetupSubTab) => void;
}

interface SetupStatus {
  missing: string[] | null;
  activeItems: number | null;
  groups: { id: string; name: string }[] | null;
  groupsWithScheme: string[];
}

type CardState = 'done' | 'attention' | 'error' | 'unknown';

/** What the settings form refuses to save empty; missing, it is an error rather than a gap. */
const BLOCKING = ['legalName', 'paymentCode'];

const EMPTY: SetupStatus = { missing: null, activeItems: null, groups: null, groupsWithScheme: [] };

/**
 * What the three parts of billing need, counted from the calls the parts already make.
 * Each is fetched on its own, so one failing leaves the other cards with their numbers.
 */
const useSetupStatus = (kindergartenId: string) => {
  const [status, setStatus] = useState<SetupStatus>(EMPTY);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const patch = (next: Partial<SetupStatus>) => !cancelled && setStatus(prev => ({ ...prev, ...next }));
    getBillingSettings(kindergartenId)
      .then(result => patch({ missing: result.results?.missingForInvoicing ?? [] }))
      .catch(() => undefined);
    getActiveBillingItems(kindergartenId)
      .then(result => patch({ activeItems: result.results?.length ?? 0 }))
      .catch(() => undefined);
    Promise.all([getGroupsWithLocationName(kindergartenId), getBillingSchemes(kindergartenId)])
      .then(([groups, schemes]) =>
        patch({
          groups: (groups.results ?? []).map(group => ({ id: group.id, name: group.name })),
          groupsWithScheme: (schemes.results ?? []).flatMap(scheme => scheme.groups.map(group => group.id))
        })
      )
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [kindergartenId, nonce]);

  return { status, reload: useCallback(() => setNonce(n => n + 1), []) };
};

const CIRCLE: Record<CardState | 'selected', string> = {
  done: 'success.main',
  error: 'error.main',
  attention: 'warning.main',
  selected: 'primary.main',
  unknown: 'field.border'
};

/**
 * Billing configuration, in one place: what the invoice says, what can be charged, and
 * what each group is charged - in that order, because each needs the one before it.
 *
 * The three parts are cards rather than plain tabs so each says, before it is opened,
 * whether it is ready and what it lacks; the same cards as the month's steps on Invoices.
 * The page above owns the heading and the selected part, which stays in `?tab=`.
 */
const BillingSetupTab: React.FC<BillingSetupTabProps> = ({ kindergartenId, subTab, onSubTabChange }) => {
  const { t } = useTranslation();
  const { status, reload } = useSetupStatus(kindergartenId);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  const groupsWithout = (status.groups ?? []).filter(group => !status.groupsWithScheme.includes(group.id));
  const withScheme = (status.groups?.length ?? 0) - groupsWithout.length;

  const cards: { tab: BillingSetupSubTab; title: string; state: CardState; line: string | null }[] = [
    {
      tab: BillingSetupSubTab.Settings,
      title: t('billing.setup.settings'),
      state:
        status.missing === null
          ? 'unknown'
          : status.missing.some(key => BLOCKING.includes(key))
            ? 'error'
            : status.missing.length
              ? 'attention'
              : 'done',
      line:
        status.missing === null
          ? null
          : status.missing.length
            ? t('billing.setup.status.settingsMissing', {
                missing: status.missing.map(key => (MISSING_LABELS[key] ? t(MISSING_LABELS[key]) : key)).join(', ')
              })
            : t('billing.setup.status.settingsReady')
    },
    {
      tab: BillingSetupSubTab.Items,
      title: t('billing.setup.items'),
      state: status.activeItems === null ? 'unknown' : status.activeItems ? 'done' : 'attention',
      line: status.activeItems === null ? null : t('billing.setup.status.items', { count: status.activeItems })
    },
    {
      tab: BillingSetupSubTab.Schemes,
      title: t('billing.setup.schemes'),
      state: status.groups === null ? 'unknown' : status.groups.length && !groupsWithout.length ? 'done' : 'attention',
      line:
        status.groups === null
          ? null
          : t('billing.setup.status.schemes', { count: status.groups.length, withScheme }) +
            (groupsWithout.length
              ? ` · ${t('billing.setup.status.schemesWithout', { names: groupsWithout.map(group => group.name).join(', ') })}`
              : '')
    }
  ];

  const focusTab = (index: number) => {
    const next = (index + cards.length) % cards.length;
    onSubTabChange(cards[next].tab);
    tabs.current[next]?.focus();
  };

  return (
    <Box>
      <Box
        role="tablist"
        aria-label={t('billing.setup.ariaLabel')}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}
      >
        {cards.map((card, index) => {
          const selected = card.tab === subTab;
          const circle = card.state !== 'unknown' ? CIRCLE[card.state] : selected ? CIRCLE.selected : CIRCLE.unknown;
          return (
            <ButtonBase
              key={card.tab}
              ref={element => {
                tabs.current[index] = element;
              }}
              role="tab"
              id={`billing-setup-tab-${card.tab}`}
              aria-selected={selected}
              aria-controls="billing-setup-panel"
              tabIndex={selected ? 0 : -1}
              onClick={() => onSubTabChange(card.tab)}
              onKeyDown={event => {
                if (event.key === 'ArrowRight') {
                  event.preventDefault();
                  focusTab(index + 1);
                } else if (event.key === 'ArrowLeft') {
                  event.preventDefault();
                  focusTab(index - 1);
                }
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                gap: 1,
                borderRadius: 4,
                p: '16px 20px',
                border: 1,
                borderColor: 'line',
                ...(selected ? { borderWidth: 2, borderColor: 'primary.main', bgcolor: 'surface.tint', p: '15px 19px' } : {}),
                '&:hover': selected ? undefined : { borderColor: 'field.hover' },
                '&.Mui-focusVisible': { outline: 2, outlineStyle: 'solid', outlineColor: 'primary.main', outlineOffset: 2 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: circle,
                    color: palette.common.white,
                    fontSize: 13,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  {card.state === 'done' ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                </Box>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>{card.title}</Typography>
              </Box>
              {card.line && (
                <Typography
                  sx={{
                    fontSize: 14,
                    color:
                      card.state === 'error'
                        ? 'error.main'
                        : card.state === 'attention'
                          ? 'warning.main'
                          : card.state === 'done' && index === 0
                            ? 'success.main'
                            : 'text.secondary'
                  }}
                >
                  {card.line}
                </Typography>
              )}
            </ButtonBase>
          );
        })}
      </Box>

      <Box role="tabpanel" id="billing-setup-panel" aria-labelledby={`billing-setup-tab-${subTab}`}>
        {subTab === BillingSetupSubTab.Settings && <BillingSettingsTab kindergartenId={kindergartenId} onSaved={reload} />}
        {subTab === BillingSetupSubTab.Items && <BillingItemsTab kindergartenId={kindergartenId} onChanged={reload} />}
        {subTab === BillingSetupSubTab.Schemes && <BillingSchemesTab kindergartenId={kindergartenId} onChanged={reload} />}
      </Box>
    </Box>
  );
};

export default BillingSetupTab;
